import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';

// -------------------------------------
// Barra de Tabs simplificada
// -------------------------------------
const StyledTabs = styled(Tabs)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '40px',
    height: '40px',
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(40, 40, 42, 0.94)'
        : 'rgba(242, 242, 247, 1)'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    padding: '6px',
    borderRadius: '24px',
    position: 'relative',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : 'none',
  
    '& .MuiTabs-scroller': {
      overflow: 'visible',
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTabs-flexContainer': {
      gap: '4px',
      height: '28px',
    },
  };
});

// -------------------------------------
// Contenedor principal simplificado
// -------------------------------------
const NavigationContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? '#1C1C1E'
        : 'rgba(118, 118, 128, 0.20)'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    borderRadius: '30px',
    overflow: 'hidden',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : 'none',
  };
});

// -------------------------------------
// Indicador del tab activo
// -------------------------------------
const TabIndicator = styled('div')(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    height: '28px',
    width: '74px',
    borderRadius: '24px',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.2)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(118, 118, 128, 0.24)'
        : '#FFFFFF'),
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 0,
    boxShadow: isWallpaperMode
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? 'none'
        : '0 2px 8px rgba(0, 0, 0, 0.04)'),
  };
});

// -------------------------------------
// Tab individual simplificado
// -------------------------------------
const StyledTab = styled(Tab)(({ theme }) => {
  const isWallpaper = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '28px',
    height: '28px',
    width: '74px',
    minWidth: '74px',
    padding: '0 8px',
    borderRadius: '24px',
    // Blanco en wallpaper cuando NO está seleccionado
    color: isWallpaper
      ? 'rgba(255, 255, 255, 0.7)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.7)'
        : '#666666'),
    fontSize: '14px',
    fontWeight: 500,
    textTransform: 'none',
    zIndex: 1,
    transition: 'all 0.2s',
    backgroundColor: 'transparent',

    '&.Mui-selected': {
      // Blanco en wallpaper cuando ESTÁ seleccionado, azul en otros
      color: isWallpaper ? '#FFFFFF' : '#007BFE',
    },

    '&:hover:not(.Mui-selected)': {
      color: isWallpaper ? '#FFFFFF' : '#007BFE',
      backgroundColor: 'transparent',
    },
  };
});

// -------------------------------------
// Botones de navegación simplificados
// -------------------------------------
const NavigationButton = styled('button')(({ theme }) => {
  const isWallpaper = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '28px',
    height: '28px',
    width: '28px',
    minWidth: '28px',
    padding: '0',
    marginRight: '6px',
    marginLeft: '6px',
    border: 'none',
    borderRadius: '30px',
    backgroundColor: 'transparent',
    // Blanco en wallpaper, azul en otros
    color: isWallpaper ? '#FFFFFF' : '#007BFE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 1,
    transition: 'all 0.2s',
    outline: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      opacity: 0.8,
    },
    '&:active': {
      transform: 'scale(0.95)',
      backgroundColor: 'transparent',
    },
  };
});

// -------------------------------------
// Componente principal
// -------------------------------------
const NavigationBar = ({
  onNavigate,
  activeSection,
  showUpsInNav,
  showEduInNav,
  showFinInNav,
  showHealthInNav,
  showFitInNav,
  selectedApps = ['home', 'chat']
}) => {
  // const theme = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  const VISIBLE_COUNT = 2;
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: VISIBLE_COUNT });
  const tabsRef = useRef(null);

  const allNavItems = useMemo(() => ({
    home: { id: 'home', label: 'Home' },
    chat: { id: 'chat', label: 'Chat' },
    ups: { id: 'ups', label: 'Ups' },
    edu: { id: 'edu', label: 'Edu' },
    fin: { id: 'fin', label: 'Fin' },
    health: { id: 'health', label: 'Health' },
    fit: { id: 'fit', label: 'Fit' },
    legal: { id: 'legal', label: 'Legal' },
    job: { id: 'job', label: 'Job' },
    translate: { id: 'translate', label: 'Translate' },
    store: { id: 'store', label: 'Store' },
    vision: { id: 'vision', label: 'Vision' }
  }), []);

  const menuItems = useMemo(() => {
    let appsToShow = selectedApps;

    const filteredItems = appsToShow
      .filter(appId => {
        if (appId === 'ups') return showUpsInNav;
        if (appId === 'edu') return showEduInNav;
        if (appId === 'fin') return showFinInNav;
        if (appId === 'health') return showHealthInNav;
        if (appId === 'fit') return showFitInNav;
        return true;
      })
      .map(appId => allNavItems[appId])
      .filter(item => item);

    const orderedItems = [];
    const homeItem = filteredItems.find(item => item.id === 'home');
    if (homeItem) orderedItems.push(homeItem);

    const chatItem = filteredItems.find(item => item.id === 'chat');
    if (chatItem) orderedItems.push(chatItem);

    const otherItems = filteredItems.filter(item => item.id !== 'home' && item.id !== 'chat');
    orderedItems.push(...otherItems);

    return orderedItems;
  }, [selectedApps, showUpsInNav, showEduInNav, showFinInNav, showHealthInNav, showFitInNav, allNavItems]);

  const visibleMenuItems = isMobile
    ? menuItems.slice(visibleRange.start, visibleRange.end)
    : menuItems;

  const showLeftArrow = isMobile && visibleRange.start > 0;
  const showRightArrow = isMobile && visibleRange.end < menuItems.length;

  const getRelativeActiveIndex = useCallback(() => {
    const absoluteIndex = menuItems.findIndex(
      (item) => item.id === activeSection
    );
    if (absoluteIndex === -1) return -1;
    const relativeIndex = absoluteIndex - visibleRange.start;
    // Asegurar que el índice relativo esté dentro del rango visible
    if (relativeIndex < 0 || relativeIndex >= (visibleRange.end - visibleRange.start)) {
      return -1;
    }
    return relativeIndex;
  }, [menuItems, activeSection, visibleRange.start, visibleRange.end]);

  const getIndicatorStyle = useCallback(() => {
    const activeIdx = getRelativeActiveIndex();
    if (activeIdx < 0) return {};
    const offset = activeIdx * (74 + 4);
    return { transform: `translateX(${offset}px)` };
  }, [getRelativeActiveIndex]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setVisibleRange({ start: 0, end: menuItems.length });
      return;
    }
    const activeIdx = menuItems.findIndex(
      (item) => item.id === activeSection
    );
    if (activeIdx === -1) return;

    let start = activeIdx - Math.floor(VISIBLE_COUNT / 2);
    if (start < 0) start = 0;
    let end = start + VISIBLE_COUNT;
    if (end > menuItems.length) {
      end = menuItems.length;
      start = end - VISIBLE_COUNT;
      if (start < 0) start = 0;
    }
    setVisibleRange({ start, end });
  }, [activeSection, menuItems, isMobile]);

  const handleHomeNavigation = () => {
    onNavigate('home');
  };

  const handleTabChange = useCallback(
    (_event, newValue) => {
      // Ignorar si newValue es false (cuando no hay tab seleccionado)
      if (newValue === false) return;
      const realIndex = newValue + visibleRange.start;
      const section = menuItems[realIndex].id;
      onNavigate(section);
    },
    [menuItems, visibleRange, onNavigate]
  );

  const handleTabClick = useCallback(
    (sectionId) => (event) => {
      event.stopPropagation();
      onNavigate(sectionId);
    },
    [onNavigate]
  );

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '1872px',
        height: '40px',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <NavigationContainer>
        {showLeftArrow && (
          <NavigationButton
            onClick={handleHomeNavigation}
            aria-label="Go to Home"
          >
            <ChevronLeft size={16} strokeWidth={2.5} />
          </NavigationButton>
        )}

        <StyledTabs
          ref={tabsRef}
          value={getRelativeActiveIndex() === -1 ? false : Math.max(0, Math.min(getRelativeActiveIndex(), visibleMenuItems.length - 1))}
          onChange={getRelativeActiveIndex() === -1 ? undefined : handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          aria-label="navigation tabs"
        >
          <TabIndicator style={getIndicatorStyle()} />

          {visibleMenuItems.map((item, index) => (
            <StyledTab
              key={item.id}
              label={item.label}
              disableRipple
              value={index}
              onClick={handleTabClick(item.id)}
            />
          ))}
        </StyledTabs>

        {isMobile ? (
          showRightArrow ? (
            <NavigationButton
              onClick={() =>
                onNavigate(menuItems[visibleRange.end].id)
              }
              aria-label="Next section"
            >
              <ChevronRight size={16} strokeWidth={2.5} />
            </NavigationButton>
          ) : (
            <NavigationButton
              onClick={() => onNavigate('promo')}
              aria-label="Add new"
            >
              <Plus size={16} strokeWidth={2.5} />
            </NavigationButton>
          )
        ) : (
          <NavigationButton
            onClick={() => onNavigate('promo')}
            aria-label="Add new"
          >
            <Plus size={16} strokeWidth={2.5} />
          </NavigationButton>
        )}
      </NavigationContainer>
    </Box>
  );
};

export default NavigationBar;